/**
 * "Ditt mönster" (pedagogy spec §7.3.8): lost points per cause group as text
 * with `aria-hidden` bars, largest first, and the share lost before any
 * calculation in a polite live region, so it is announced as causes change.
 * "Vet inte" belongs to no bar; when it is all the student chose, the card
 * says so instead of asking for causes.
 */
import clsx from "clsx";
import {
  beforeCalculation,
  lostByGroup,
  onlyUnknownCauses,
  type CauseMap,
  type PatternGroup,
  type WrapperProblem,
} from "@shared/examWrapper";
import { Card } from "@client/components/ui";
import { useLang, useT, useTp, type TFunction } from "@client/i18n";
import { formatNumber, formatPoints } from "@client/lib/format";

export interface PatternCardProps {
  problems: readonly WrapperProblem[];
  causes: CauseMap;
}

function groupLabel(group: PatternGroup, t: TFunction): string {
  if (group === "principle") return t("exam.wrapper.group.principle");
  if (group === "setup") return t("exam.wrapper.group.setup");
  if (group === "unassigned") return t("exam.wrapper.group.unassigned");
  return t(`exam.wrapper.cause.${group}`);
}

export function PatternCard({ problems, causes }: PatternCardProps) {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const groups = lostByGroup(problems, causes);
  const before = beforeCalculation(problems, causes);
  const onlyUnknown = onlyUnknownCauses(problems, causes);
  const largest = Math.max(0, ...groups.map((g) => g.points));
  const number = (value: number) => formatNumber(value, lang, { maxDecimals: 1 });

  return (
    <Card
      title={t("exam.wrapper.pattern.title")}
      description={t("exam.wrapper.pattern.description")}
    >
      {before.assigned === 0 ? (
        <p className="text-sm text-ink-700 dark:text-ink-200">
          {t(onlyUnknown ? "exam.wrapper.pattern.onlyUnknown" : "exam.wrapper.pattern.empty")}
        </p>
      ) : (
        <ul className="space-y-3">
          {groups.map(({ group, points }) => (
            <li key={group}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-ink-800 dark:text-ink-100">{groupLabel(group, t)}</span>
                <span className="font-medium tabular-nums text-ink-900 dark:text-ink-50">
                  {formatPoints(points, lang)}
                </span>
              </div>
              <div aria-hidden="true" className="mt-1 h-2 rounded-full bg-ink-200 dark:bg-ink-800">
                <div
                  className={clsx(
                    "h-full rounded-full",
                    group === "unassigned"
                      ? "bg-ink-400 dark:bg-ink-500"
                      : "bg-accent-600 dark:bg-accent-400",
                  )}
                  style={{ width: `${(points / largest) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
      <p aria-live="polite" className="mt-4 text-sm text-ink-700 dark:text-ink-200">
        {before.assigned > 0
          ? tp("exam.wrapper.pattern.beforeCalc", before.assigned, {
              assigned: number(before.assigned),
              points: number(before.points),
            })
          : ""}
      </p>
    </Card>
  );
}
